import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  FileText,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload } from
'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface Assignment {
  ID: number;
  class_id: number;
  title: string;
  description: string;
  due_date: string;
  max_points: number;
  assignment_type: string;
  instructions: string;
}

interface Submission {
  ID: number;
  assignment_id: number;
  student_id: number;
  submission_text: string;
  submitted_at: string;
  file_id: number;
  status: string;
}

interface Grade {
  ID: number;
  submission_id: number;
  points_earned: number;
  feedback: string;
  graded_at: string;
  graded_by: number;
}

const AssignmentManager: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [classes, setClasses] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [isNewAssignmentDialogOpen, setIsNewAssignmentDialogOpen] = useState(false);
  const [isGradingDialogOpen, setIsGradingDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState<Date>();

  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    instructions: '',
    due_date: '',
    max_points: 100,
    assignment_type: 'homework',
    class_id: ''
  });

  const [gradingData, setGradingData] = useState({
    points_earned: 0,
    feedback: ''
  });

  // Load data functions
  const loadClasses = async () => {
    if (!user) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage('15198', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: [
        {
          name: 'teacher_id',
          op: 'Equal',
          value: user.ID
        }]

      });

      if (error) throw error;
      setClasses(data?.List || []);
    } catch (error) {
      console.error('Error loading classes:', error);
      setClasses([]);
      toast({
        title: "Error",
        description: "Failed to load classes. Please refresh the page.",
        variant: "destructive"
      });
    }
  };

  const loadAssignments = async () => {
    try {
      const filters = selectedClass && selectedClass !== 'all' ? [
      {
        name: 'class_id',
        op: 'Equal',
        value: parseInt(selectedClass)
      }] :
      [];

      const { data, error } = await window.ezsite.apis.tablePage('15199', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: filters
      });

      if (error) throw error;
      setAssignments(data?.List || []);
    } catch (error) {
      console.error('Error loading assignments:', error);
      setAssignments([]);
    }
  };

  const loadSubmissions = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage('15201', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: []
      });

      if (error) throw error;
      setSubmissions(data?.List || []);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setSubmissions([]);
    }
  };

  const loadGrades = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage('15203', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: []
      });

      if (error) throw error;
      setGrades(data?.List || []);
    } catch (error) {
      console.error('Error loading grades:', error);
      setGrades([]);
    }
  };

  const loadStudents = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage('15200', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: []
      });

      if (error) throw error;
      setStudents(data?.List || []);
    } catch (error) {
      console.error('Error loading students:', error);
      setStudents([]);
    }
  };

  const loadEnrollments = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage('15202', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'ID',
        IsAsc: false,
        Filters: []
      });

      if (error) throw error;
      setEnrollments(data?.List || []);
    } catch (error) {
      console.error('Error loading enrollments:', error);
      setEnrollments([]);
    }
  };

  useEffect(() => {
    loadClasses();
    loadStudents();
    loadEnrollments();
  }, [user]);

  useEffect(() => {
    loadAssignments();
  }, [selectedClass]);

  useEffect(() => {
    loadSubmissions();
    loadGrades();
  }, [assignments]);

  const createAssignment = async () => {
    if (!newAssignment.title || !newAssignment.class_id || !newAssignment.due_date) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      const { error } = await window.ezsite.apis.tableCreate('15199', {
        ...newAssignment,
        class_id: parseInt(newAssignment.class_id)
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Assignment created successfully!"
      });

      setNewAssignment({
        title: '',
        description: '',
        instructions: '',
        due_date: '',
        max_points: 100,
        assignment_type: 'homework',
        class_id: ''
      });
      setIsNewAssignmentDialogOpen(false);
      loadAssignments();
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast({
        title: "Error",
        description: "Failed to create assignment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const gradeSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      setLoading(true);
      const { error } = await window.ezsite.apis.tableCreate('15203', {
        submission_id: selectedSubmission.ID,
        points_earned: gradingData.points_earned,
        feedback: gradingData.feedback,
        graded_at: new Date().toISOString(),
        graded_by: user?.ID
      });

      if (error) throw error;

      // Update submission status
      await window.ezsite.apis.tableUpdate('15201', {
        ID: selectedSubmission.ID,
        status: 'graded'
      });

      toast({
        title: "Success",
        description: "Submission graded successfully!"
      });

      setGradingData({ points_earned: 0, feedback: '' });
      setIsGradingDialogOpen(false);
      setSelectedSubmission(null);
      loadSubmissions();
      loadGrades();
    } catch (error) {
      console.error('Error grading submission:', error);
      toast({
        title: "Error",
        description: "Failed to grade submission. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getSubmissionsForAssignment = (assignmentId: number) => {
    if (!assignmentId || !submissions || !Array.isArray(submissions)) {
      return [];
    }
    return submissions.filter((s) => s && s.assignment_id === assignmentId);
  };

  const getGradeForSubmission = (submissionId: number) => {
    if (!submissionId || !grades || !Array.isArray(grades)) {
      return null;
    }
    return grades.find((g) => g && g.submission_id === submissionId);
  };

  const getStudentName = (studentId: number) => {
    if (!studentId || !students || !Array.isArray(students)) {
      return 'Unknown Student';
    }
    const student = students.find((s) => s && s.ID === studentId);
    return student && student.student_name ? student.student_name : 'Unknown Student';
  };

  const getClassName = (classId: number) => {
    if (!classId || !classes || !Array.isArray(classes)) {
      return 'Unknown Class';
    }
    const cls = classes.find((c) => c && c.ID === classId);
    return cls && cls.class_name ? cls.class_name : 'Unknown Class';
  };

  const getAssignmentStats = (assignment: Assignment) => {
    if (!assignment || !assignments || !Array.isArray(submissions)) {
      return { total: 0, graded: 0, pending: 0 };
    }
    const assignmentSubmissions = getSubmissionsForAssignment(assignment.ID);
    const graded = assignmentSubmissions.filter((s) => s && s.status === 'graded').length;
    const pending = assignmentSubmissions.filter((s) => s && s.status === 'submitted').length;

    return { total: assignmentSubmissions.length, graded, pending };
  };

  const isOverdue = (dueDate: string) => {
    if (!dueDate) return false;
    try {
      return new Date(dueDate) < new Date();
    } catch {
      return false;
    }
  };

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') {
      return 'UK';
    }
    try {
      return name.split(' ').
      filter((n) => n && n.length > 0).
      map((n) => n[0]).
      join('').
      toUpperCase().
      slice(0, 2) || 'UK';
    } catch {
      return 'UK';
    }
  };

  return (
    <div className="space-y-6" data-id="8k08w4r4r" data-path="src/components/assignments/AssignmentManager.tsx">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between" data-id="ct0j6gw9o" data-path="src/components/assignments/AssignmentManager.tsx">
        <div data-id="k35a0xszz" data-path="src/components/assignments/AssignmentManager.tsx">
          <h1 className="text-2xl font-bold text-gray-900" data-id="gty2utu4a" data-path="src/components/assignments/AssignmentManager.tsx">Assignment Manager</h1>
          <p className="text-gray-600" data-id="9ogaw3l6h" data-path="src/components/assignments/AssignmentManager.tsx">Create and manage assignments for your classes</p>
        </div>
        <Dialog open={isNewAssignmentDialogOpen} onOpenChange={setIsNewAssignmentDialogOpen} data-id="18djwv69n" data-path="src/components/assignments/AssignmentManager.tsx">
          <DialogTrigger asChild data-id="7fwbadb14" data-path="src/components/assignments/AssignmentManager.tsx">
            <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" data-id="p83ux1r8m" data-path="src/components/assignments/AssignmentManager.tsx">
              <Plus className="h-4 w-4 mr-2" data-id="d0om9cize" data-path="src/components/assignments/AssignmentManager.tsx" />
              New Assignment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-id="aqd4hkc5f" data-path="src/components/assignments/AssignmentManager.tsx">
            <DialogHeader data-id="t2ti50jl5" data-path="src/components/assignments/AssignmentManager.tsx">
              <DialogTitle data-id="tg9tp2tyb" data-path="src/components/assignments/AssignmentManager.tsx">Create New Assignment</DialogTitle>
              <DialogDescription data-id="h6dusfx86" data-path="src/components/assignments/AssignmentManager.tsx">
                Add a new assignment for your students
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4" data-id="u2esotyel" data-path="src/components/assignments/AssignmentManager.tsx">
              <div className="grid grid-cols-2 gap-4" data-id="6fegy5h7p" data-path="src/components/assignments/AssignmentManager.tsx">
                <div data-id="77nybhej0" data-path="src/components/assignments/AssignmentManager.tsx">
                  <Label htmlFor="title" data-id="7hvvj6oyb" data-path="src/components/assignments/AssignmentManager.tsx">Assignment Title *</Label>
                  <Input
                    id="title"
                    value={newAssignment.title}
                    onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })}
                    placeholder="Enter assignment title" data-id="4k1spul0s" data-path="src/components/assignments/AssignmentManager.tsx" />

                </div>
                <div data-id="8ss0h773q" data-path="src/components/assignments/AssignmentManager.tsx">
                  <Label htmlFor="class_id" data-id="5d0lzdobr" data-path="src/components/assignments/AssignmentManager.tsx">Class *</Label>
                  <Select value={newAssignment.class_id} onValueChange={(value) => setNewAssignment({ ...newAssignment, class_id: value })} data-id="du3f6blnm" data-path="src/components/assignments/AssignmentManager.tsx">
                    <SelectTrigger data-id="5p8ca09uc" data-path="src/components/assignments/AssignmentManager.tsx">
                      <SelectValue placeholder="Select class" data-id="wdxrtienj" data-path="src/components/assignments/AssignmentManager.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="czfysfs9d" data-path="src/components/assignments/AssignmentManager.tsx">
                      {classes && classes.filter((cls) => cls && cls.ID && cls.class_name).map((cls) =>
                      <SelectItem key={cls.ID} value={cls.ID.toString()} data-id="07mdhjcto" data-path="src/components/assignments/AssignmentManager.tsx">
                          {cls.class_name} - {cls.subject || 'No Subject'}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div data-id="i5twhu1vd" data-path="src/components/assignments/AssignmentManager.tsx">
                <Label htmlFor="description" data-id="i8p03ye5r" data-path="src/components/assignments/AssignmentManager.tsx">Description</Label>
                <Textarea
                  id="description"
                  value={newAssignment.description}
                  onChange={(e) => setNewAssignment({ ...newAssignment, description: e.target.value })}
                  placeholder="Brief description of the assignment"
                  rows={3} data-id="ye69gq889" data-path="src/components/assignments/AssignmentManager.tsx" />

              </div>
              
              <div data-id="k6p58c81c" data-path="src/components/assignments/AssignmentManager.tsx">
                <Label htmlFor="instructions" data-id="emi8fa1pe" data-path="src/components/assignments/AssignmentManager.tsx">Instructions</Label>
                <Textarea
                  id="instructions"
                  value={newAssignment.instructions}
                  onChange={(e) => setNewAssignment({ ...newAssignment, instructions: e.target.value })}
                  placeholder="Detailed instructions for students"
                  rows={4} data-id="nk6dnkhwr" data-path="src/components/assignments/AssignmentManager.tsx" />

              </div>
              
              <div className="grid grid-cols-3 gap-4" data-id="4edtoe1jq" data-path="src/components/assignments/AssignmentManager.tsx">
                <div data-id="izl12b6qp" data-path="src/components/assignments/AssignmentManager.tsx">
                  <Label htmlFor="assignment_type" data-id="4zq4uv14t" data-path="src/components/assignments/AssignmentManager.tsx">Type</Label>
                  <Select value={newAssignment.assignment_type} onValueChange={(value) => setNewAssignment({ ...newAssignment, assignment_type: value })} data-id="fw2rl5rq3" data-path="src/components/assignments/AssignmentManager.tsx">
                    <SelectTrigger data-id="8mlu3ybwf" data-path="src/components/assignments/AssignmentManager.tsx">
                      <SelectValue data-id="fkd0bn43e" data-path="src/components/assignments/AssignmentManager.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="mg4sfi3ei" data-path="src/components/assignments/AssignmentManager.tsx">
                      <SelectItem value="homework" data-id="i6u3a2qus" data-path="src/components/assignments/AssignmentManager.tsx">Homework</SelectItem>
                      <SelectItem value="project" data-id="yfslmivk4" data-path="src/components/assignments/AssignmentManager.tsx">Project</SelectItem>
                      <SelectItem value="quiz" data-id="irwylc4nj" data-path="src/components/assignments/AssignmentManager.tsx">Quiz</SelectItem>
                      <SelectItem value="exam" data-id="49ymxr4u4" data-path="src/components/assignments/AssignmentManager.tsx">Exam</SelectItem>
                      <SelectItem value="lab" data-id="xkbjxfi8p" data-path="src/components/assignments/AssignmentManager.tsx">Lab</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div data-id="hdu6wxv3e" data-path="src/components/assignments/AssignmentManager.tsx">
                  <Label htmlFor="max_points" data-id="climh8uvo" data-path="src/components/assignments/AssignmentManager.tsx">Max Points</Label>
                  <Input
                    id="max_points"
                    type="number"
                    value={newAssignment.max_points}
                    onChange={(e) => setNewAssignment({ ...newAssignment, max_points: parseInt(e.target.value) || 0 })} data-id="18j7q9m1v" data-path="src/components/assignments/AssignmentManager.tsx" />

                </div>
                <div data-id="w9sld9g59" data-path="src/components/assignments/AssignmentManager.tsx">
                  <Label htmlFor="due_date" data-id="wr0n0a7xd" data-path="src/components/assignments/AssignmentManager.tsx">Due Date *</Label>
                  <Popover data-id="vwnztpyis" data-path="src/components/assignments/AssignmentManager.tsx">
                    <PopoverTrigger asChild data-id="b81z2mky9" data-path="src/components/assignments/AssignmentManager.tsx">
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )} data-id="kwhdyy3vs" data-path="src/components/assignments/AssignmentManager.tsx">

                        <CalendarIcon className="mr-2 h-4 w-4" data-id="ss1k161bp" data-path="src/components/assignments/AssignmentManager.tsx" />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" data-id="7n2x6hg7u" data-path="src/components/assignments/AssignmentManager.tsx">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(selectedDate) => {
                          setDate(selectedDate);
                          if (selectedDate) {
                            setNewAssignment({ ...newAssignment, due_date: selectedDate.toISOString() });
                          }
                        }}
                        initialFocus data-id="fxzyc7onj" data-path="src/components/assignments/AssignmentManager.tsx" />

                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            <DialogFooter data-id="dy69vc8iu" data-path="src/components/assignments/AssignmentManager.tsx">
              <Button variant="outline" onClick={() => setIsNewAssignmentDialogOpen(false)} data-id="yvhik5go7" data-path="src/components/assignments/AssignmentManager.tsx">
                Cancel
              </Button>
              <Button onClick={createAssignment} disabled={loading} data-id="k2bt2g1fx" data-path="src/components/assignments/AssignmentManager.tsx">
                {loading ? 'Creating...' : 'Create Assignment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Class Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }} data-id="qkvl8xxbd" data-path="src/components/assignments/AssignmentManager.tsx">
        <div className="flex items-center space-x-4" data-id="q0y99z6zo" data-path="src/components/assignments/AssignmentManager.tsx">
          <Label htmlFor="class-filter" data-id="4xq90swd1" data-path="src/components/assignments/AssignmentManager.tsx">Filter by Class:</Label>
          <Select value={selectedClass} onValueChange={setSelectedClass} data-id="upfq3jt77" data-path="src/components/assignments/AssignmentManager.tsx">
            <SelectTrigger className="w-[300px]" data-id="4lsobihsh" data-path="src/components/assignments/AssignmentManager.tsx">
              <SelectValue placeholder="All classes" data-id="tvetso6lo" data-path="src/components/assignments/AssignmentManager.tsx" />
            </SelectTrigger>
            <SelectContent data-id="n4bui2rul" data-path="src/components/assignments/AssignmentManager.tsx">
              <SelectItem value="all" data-id="ciyrf60is" data-path="src/components/assignments/AssignmentManager.tsx">All classes</SelectItem>
              {classes && classes.filter((cls) => cls && cls.ID && cls.class_name).map((cls) =>
              <SelectItem key={cls.ID} value={cls.ID.toString()} data-id="b5umjackz" data-path="src/components/assignments/AssignmentManager.tsx">
                  {cls.class_name} - {cls.subject || 'No Subject'}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      <Tabs defaultValue="assignments" className="w-full" data-id="s270e75m7" data-path="src/components/assignments/AssignmentManager.tsx">
        <TabsList data-id="6o57yh5iy" data-path="src/components/assignments/AssignmentManager.tsx">
          <TabsTrigger value="assignments" data-id="4mh6atiix" data-path="src/components/assignments/AssignmentManager.tsx">All Assignments ({assignments ? assignments.length : 0})</TabsTrigger>
          <TabsTrigger value="submissions" data-id="i65upwiyu" data-path="src/components/assignments/AssignmentManager.tsx">Pending Submissions</TabsTrigger>
          <TabsTrigger value="grading" data-id="wqbsnh48z" data-path="src/components/assignments/AssignmentManager.tsx">Grading</TabsTrigger>
        </TabsList>

        <TabsContent value="assignments" className="space-y-6" data-id="j1xa0rwrn" data-path="src/components/assignments/AssignmentManager.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="2n1rfm623" data-path="src/components/assignments/AssignmentManager.tsx">
            {!assignments || assignments.length === 0 ?
            <div className="col-span-full text-center py-12" data-id="470fg0puy" data-path="src/components/assignments/AssignmentManager.tsx">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="c7o6y8t7m" data-path="src/components/assignments/AssignmentManager.tsx" />
                <p className="text-gray-500" data-id="wokzrtda3" data-path="src/components/assignments/AssignmentManager.tsx">No assignments found</p>
                <p className="text-sm text-gray-400" data-id="qn4ll527r" data-path="src/components/assignments/AssignmentManager.tsx">Create your first assignment to get started</p>
              </div> :

            assignments.filter((assignment) => assignment && assignment.ID).map((assignment, index) => {
              const stats = getAssignmentStats(assignment);
              const overdue = isOverdue(assignment.due_date);

              return (
                <motion.div
                  key={assignment.ID}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }} data-id="2p1qigvfr" data-path="src/components/assignments/AssignmentManager.tsx">
                    <Card className="hover:shadow-lg transition-shadow" data-id="voe1sw92b" data-path="src/components/assignments/AssignmentManager.tsx">
                      <CardHeader data-id="tbt2bws6o" data-path="src/components/assignments/AssignmentManager.tsx">
                        <div className="flex items-center justify-between" data-id="1yqdxk06g" data-path="src/components/assignments/AssignmentManager.tsx">
                          <CardTitle className="text-lg" data-id="hyl25ly0b" data-path="src/components/assignments/AssignmentManager.tsx">{assignment.title}</CardTitle>
                          <Badge variant={overdue ? "destructive" : "secondary"} data-id="6a1fhdqp1" data-path="src/components/assignments/AssignmentManager.tsx">
                            {assignment.assignment_type}
                          </Badge>
                        </div>
                        <CardDescription data-id="03od1fhu8" data-path="src/components/assignments/AssignmentManager.tsx">
                          {getClassName(assignment.class_id)} • {assignment.max_points} pts
                        </CardDescription>
                      </CardHeader>
                      <CardContent data-id="zze9a6iwz" data-path="src/components/assignments/AssignmentManager.tsx">
                        <div className="space-y-3" data-id="ihgi0l7gw" data-path="src/components/assignments/AssignmentManager.tsx">
                          <div className="text-sm text-gray-600" data-id="ksnlinekm" data-path="src/components/assignments/AssignmentManager.tsx">
                            {assignment.description}
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-500" data-id="g6yllww3p" data-path="src/components/assignments/AssignmentManager.tsx">
                            <CalendarIcon className="h-4 w-4 mr-2" data-id="sw754frbl" data-path="src/components/assignments/AssignmentManager.tsx" />
                            Due: {assignment.due_date ? format(new Date(assignment.due_date), "PPP") : 'No due date'}
                            {overdue && <AlertCircle className="h-4 w-4 ml-2 text-red-500" data-id="bvd8vn02j" data-path="src/components/assignments/AssignmentManager.tsx" />}
                          </div>
                          
                          <div className="flex items-center justify-between" data-id="a2pfjvsbe" data-path="src/components/assignments/AssignmentManager.tsx">
                            <div className="text-sm" data-id="idwpfzk9t" data-path="src/components/assignments/AssignmentManager.tsx">
                              <span className="text-gray-600" data-id="n39ykrbgy" data-path="src/components/assignments/AssignmentManager.tsx">Submissions: </span>
                              <span className="font-medium" data-id="38blo3l64" data-path="src/components/assignments/AssignmentManager.tsx">{stats.total}</span>
                            </div>
                            <div className="flex space-x-1" data-id="vzcv8hdwr" data-path="src/components/assignments/AssignmentManager.tsx">
                              <Badge variant="outline" className="text-xs" data-id="kep6eqwgs" data-path="src/components/assignments/AssignmentManager.tsx">
                                Graded: {stats.graded}
                              </Badge>
                              <Badge variant="secondary" className="text-xs" data-id="bcnxzg6im" data-path="src/components/assignments/AssignmentManager.tsx">
                                Pending: {stats.pending}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between pt-2" data-id="vlgyas4bs" data-path="src/components/assignments/AssignmentManager.tsx">
                            <Button size="sm" variant="outline" onClick={() => setSelectedAssignment(assignment)} data-id="xjpclxrpd" data-path="src/components/assignments/AssignmentManager.tsx">
                              <Eye className="h-4 w-4 mr-1" data-id="7px4os2eg" data-path="src/components/assignments/AssignmentManager.tsx" />
                              View
                            </Button>
                            <div className="flex space-x-1" data-id="831k7a24n" data-path="src/components/assignments/AssignmentManager.tsx">
                              <Button size="sm" variant="outline" data-id="grtlbgdx0" data-path="src/components/assignments/AssignmentManager.tsx">
                                <Edit className="h-4 w-4" data-id="vcma1pf02" data-path="src/components/assignments/AssignmentManager.tsx" />
                              </Button>
                              <Button size="sm" variant="outline" data-id="na2gu6588" data-path="src/components/assignments/AssignmentManager.tsx">
                                <Trash2 className="h-4 w-4" data-id="k3sbdd4kb" data-path="src/components/assignments/AssignmentManager.tsx" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>);

            })
            }
          </div>
        </TabsContent>

        <TabsContent value="submissions" className="space-y-6" data-id="ujhbby4wp" data-path="src/components/assignments/AssignmentManager.tsx">
          <Card data-id="lvqem16ql" data-path="src/components/assignments/AssignmentManager.tsx">
            <CardHeader data-id="9ra7vyhrd" data-path="src/components/assignments/AssignmentManager.tsx">
              <CardTitle data-id="bh8ud4wil" data-path="src/components/assignments/AssignmentManager.tsx">Pending Submissions</CardTitle>
              <CardDescription data-id="7yjzwflrv" data-path="src/components/assignments/AssignmentManager.tsx">Submissions waiting for your review</CardDescription>
            </CardHeader>
            <CardContent data-id="f19t72jiy" data-path="src/components/assignments/AssignmentManager.tsx">
              <div className="space-y-4" data-id="gd158fpys" data-path="src/components/assignments/AssignmentManager.tsx">
                {submissions && submissions.filter((s) => s && s.status === 'submitted').map((submission, index) => {
                  const assignment = assignments && assignments.find((a) => a && a.ID === submission.assignment_id);

                  return (
                    <motion.div
                      key={submission.ID}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 border rounded-lg" data-id="ia0odaiae" data-path="src/components/assignments/AssignmentManager.tsx">
                      <div className="flex items-center space-x-4" data-id="zfrufwnfc" data-path="src/components/assignments/AssignmentManager.tsx">
                        <Avatar data-id="qdq674i0m" data-path="src/components/assignments/AssignmentManager.tsx">
                          <AvatarFallback data-id="4bzuw64wh" data-path="src/components/assignments/AssignmentManager.tsx">{getInitials(getStudentName(submission.student_id))}</AvatarFallback>
                        </Avatar>
                        <div data-id="tmkpc4hgc" data-path="src/components/assignments/AssignmentManager.tsx">
                          <h4 className="font-medium" data-id="k2ik693jb" data-path="src/components/assignments/AssignmentManager.tsx">{getStudentName(submission.student_id)}</h4>
                          <p className="text-sm text-gray-600" data-id="hhl2b2qg5" data-path="src/components/assignments/AssignmentManager.tsx">{assignment?.title}</p>
                          <p className="text-xs text-gray-500" data-id="i6i1e5jyn" data-path="src/components/assignments/AssignmentManager.tsx">
                            Submitted {submission.submitted_at ? format(new Date(submission.submitted_at), "PPP") : 'Unknown date'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2" data-id="00ph15mv2" data-path="src/components/assignments/AssignmentManager.tsx">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setGradingData({
                              points_earned: assignment?.max_points || 0,
                              feedback: ''
                            });
                            setIsGradingDialogOpen(true);
                          }} data-id="dv394215n" data-path="src/components/assignments/AssignmentManager.tsx">
                          <CheckCircle className="h-4 w-4 mr-1" data-id="x2xvqmzfq" data-path="src/components/assignments/AssignmentManager.tsx" />
                          Grade
                        </Button>
                        <Button size="sm" variant="outline" data-id="ocsk9g3rh" data-path="src/components/assignments/AssignmentManager.tsx">
                          <Eye className="h-4 w-4 mr-1" data-id="386khnose" data-path="src/components/assignments/AssignmentManager.tsx" />
                          View
                        </Button>
                      </div>
                    </motion.div>);

                })}
                {(!submissions || submissions.filter((s) => s && s.status === 'submitted').length === 0) &&
                <div className="text-center py-8" data-id="9za767i2k" data-path="src/components/assignments/AssignmentManager.tsx">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="6k23yah6g" data-path="src/components/assignments/AssignmentManager.tsx" />
                    <p className="text-gray-500" data-id="jkzk2t2an" data-path="src/components/assignments/AssignmentManager.tsx">No pending submissions</p>
                  </div>
                }
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="grading" className="space-y-6" data-id="4lhxm8ay4" data-path="src/components/assignments/AssignmentManager.tsx">
          <Card data-id="vljk1db7p" data-path="src/components/assignments/AssignmentManager.tsx">
            <CardHeader data-id="roqdyfx32" data-path="src/components/assignments/AssignmentManager.tsx">
              <CardTitle data-id="ksdbuv816" data-path="src/components/assignments/AssignmentManager.tsx">Recent Grades</CardTitle>
              <CardDescription data-id="h491mmb24" data-path="src/components/assignments/AssignmentManager.tsx">Recently graded submissions</CardDescription>
            </CardHeader>
            <CardContent data-id="srbil4lw5" data-path="src/components/assignments/AssignmentManager.tsx">
              <div className="space-y-4" data-id="1a9h65y1p" data-path="src/components/assignments/AssignmentManager.tsx">
                {submissions && submissions.filter((s) => s && s.status === 'graded').slice(0, 10).map((submission, index) => {
                  const assignment = assignments && assignments.find((a) => a && a.ID === submission.assignment_id);
                  const grade = getGradeForSubmission(submission.ID);

                  return (
                    <motion.div
                      key={submission.ID}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 border rounded-lg" data-id="tktoaw8e2" data-path="src/components/assignments/AssignmentManager.tsx">
                      <div className="flex items-center space-x-4" data-id="c1etn3xw0" data-path="src/components/assignments/AssignmentManager.tsx">
                        <Avatar data-id="1lw9jdv2a" data-path="src/components/assignments/AssignmentManager.tsx">
                          <AvatarFallback data-id="0pbs3d0db" data-path="src/components/assignments/AssignmentManager.tsx">{getInitials(getStudentName(submission.student_id))}</AvatarFallback>
                        </Avatar>
                        <div data-id="ofcp3p9nc" data-path="src/components/assignments/AssignmentManager.tsx">
                          <h4 className="font-medium" data-id="flaf7hcv5" data-path="src/components/assignments/AssignmentManager.tsx">{getStudentName(submission.student_id)}</h4>
                          <p className="text-sm text-gray-600" data-id="zuyma63xi" data-path="src/components/assignments/AssignmentManager.tsx">{assignment?.title}</p>
                          <p className="text-xs text-gray-500" data-id="kdlkx9oja" data-path="src/components/assignments/AssignmentManager.tsx">
                            Graded {grade && grade.graded_at ? format(new Date(grade.graded_at), "PPP") : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4" data-id="k1mj73jvh" data-path="src/components/assignments/AssignmentManager.tsx">
                        <div className="text-right" data-id="fyvon1s6g" data-path="src/components/assignments/AssignmentManager.tsx">
                          <p className="font-medium" data-id="f3ucase9n" data-path="src/components/assignments/AssignmentManager.tsx">
                            {grade?.points_earned || 0} / {assignment?.max_points || 0}
                          </p>
                          <p className="text-sm text-gray-500" data-id="cp0hohp10" data-path="src/components/assignments/AssignmentManager.tsx">
                            {Math.round((grade?.points_earned || 0) / (assignment?.max_points || 1) * 100)}%
                          </p>
                        </div>
                        <Button size="sm" variant="outline" data-id="h46qjz9tk" data-path="src/components/assignments/AssignmentManager.tsx">
                          <Eye className="h-4 w-4 mr-1" data-id="ihdaiivys" data-path="src/components/assignments/AssignmentManager.tsx" />
                          View
                        </Button>
                      </div>
                    </motion.div>);

                })}
                {(!submissions || submissions.filter((s) => s && s.status === 'graded').length === 0) &&
                <div className="text-center py-8" data-id="mq1cf40yk" data-path="src/components/assignments/AssignmentManager.tsx">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="oairf8xue" data-path="src/components/assignments/AssignmentManager.tsx" />
                    <p className="text-gray-500" data-id="u75k3gf98" data-path="src/components/assignments/AssignmentManager.tsx">No graded submissions yet</p>
                  </div>
                }
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Grading Dialog */}
      <Dialog open={isGradingDialogOpen} onOpenChange={setIsGradingDialogOpen} data-id="7vxvp2l19" data-path="src/components/assignments/AssignmentManager.tsx">
        <DialogContent data-id="v0rv9cijg" data-path="src/components/assignments/AssignmentManager.tsx">
          <DialogHeader data-id="vnbuldy4l" data-path="src/components/assignments/AssignmentManager.tsx">
            <DialogTitle data-id="9nunyjjj6" data-path="src/components/assignments/AssignmentManager.tsx">Grade Submission</DialogTitle>
            <DialogDescription data-id="c9w3bcca2" data-path="src/components/assignments/AssignmentManager.tsx">
              Grade the submission from {selectedSubmission ? getStudentName(selectedSubmission.student_id) : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedSubmission &&
          <div className="space-y-4" data-id="cf0anw8in" data-path="src/components/assignments/AssignmentManager.tsx">
              <div data-id="5ervjfb2j" data-path="src/components/assignments/AssignmentManager.tsx">
                <Label data-id="fex7ymh3f" data-path="src/components/assignments/AssignmentManager.tsx">Submission Text:</Label>
                <div className="bg-gray-50 p-3 rounded-lg mt-1" data-id="8v9okqcbh" data-path="src/components/assignments/AssignmentManager.tsx">
                  <p className="text-sm" data-id="15681yiy7" data-path="src/components/assignments/AssignmentManager.tsx">{selectedSubmission.submission_text || 'No text submission'}</p>
                </div>
              </div>
              
              <div data-id="edyl6jbk3" data-path="src/components/assignments/AssignmentManager.tsx">
                <Label htmlFor="points" data-id="7kydggrnn" data-path="src/components/assignments/AssignmentManager.tsx">Points Earned</Label>
                <Input
                id="points"
                type="number"
                value={gradingData.points_earned}
                onChange={(e) => setGradingData({ ...gradingData, points_earned: parseInt(e.target.value) || 0 })}
                max={assignments && assignments.find((a) => a && a.ID === selectedSubmission.assignment_id)?.max_points || 100} data-id="gfxmoeb5f" data-path="src/components/assignments/AssignmentManager.tsx" />

                <p className="text-sm text-gray-500 mt-1" data-id="e5xeelesr" data-path="src/components/assignments/AssignmentManager.tsx">
                  Out of {assignments && assignments.find((a) => a && a.ID === selectedSubmission.assignment_id)?.max_points || 100} points
                </p>
              </div>
              
              <div data-id="oy9j58110" data-path="src/components/assignments/AssignmentManager.tsx">
                <Label htmlFor="feedback" data-id="jqa33uahw" data-path="src/components/assignments/AssignmentManager.tsx">Feedback</Label>
                <Textarea
                id="feedback"
                value={gradingData.feedback}
                onChange={(e) => setGradingData({ ...gradingData, feedback: e.target.value })}
                placeholder="Provide feedback for the student"
                rows={4} data-id="zb4yev2pk" data-path="src/components/assignments/AssignmentManager.tsx" />

              </div>
            </div>
          }
          <DialogFooter data-id="ag5yhze3f" data-path="src/components/assignments/AssignmentManager.tsx">
            <Button variant="outline" onClick={() => setIsGradingDialogOpen(false)} data-id="bt3ir5y8r" data-path="src/components/assignments/AssignmentManager.tsx">
              Cancel
            </Button>
            <Button onClick={gradeSubmission} disabled={loading} data-id="gyb9dy7q8" data-path="src/components/assignments/AssignmentManager.tsx">
              {loading ? 'Submitting...' : 'Submit Grade'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default AssignmentManager;